import React, { useEffect, useState } from 'react';
import {
  Mail, MessageSquare, Plus, Trash2, Edit2, Zap, ChevronRight,
  Play, Pause, Loader, X, CheckCircle, AlertTriangle,
  ChevronDown
} from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import Tabs from '../../components/ui/Tabs';

import PageHeader from '../../components/PageHeader';
import { PAGE, PageLoader } from '../../components/crm/ui';
const TRIGGERS = [
  { value: 'form_submission', label: 'On form submission' },
  { value: 'lead_created', label: 'On lead created' },
  { value: 'deal_created', label: 'On deal created' },
  { value: 'score_threshold', label: 'When lead score ≥ threshold' },
  { value: 'task_overdue', label: 'Task overdue by 24h' },
  { value: 'manual', label: 'Manual trigger' },
];

const TYPE_META = {
  email: { label: 'Email', icon: Mail, color: '#0176D3', bg: '#EAF4FE', border: '#B8DAF3' },
  sms: { label: 'SMS', icon: MessageSquare, color: '#2E844A', bg: '#EBF3ED', border: '#C4DCC8' },
  mixed: { label: 'Mixed', icon: Zap, color: '#A094ED', bg: '#F1EFFD', border: '#D6D0F8' },
};

const STATUS_BADGE = {
  active: 'sf-badge-success',
  paused: 'sf-badge-warning',
  draft: 'sf-badge-neutral',
};

function StyledSelect({ value, onChange, options, disabled }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="w-full appearance-none bg-white border border-[#D8DDE6] rounded px-4 h-9 pr-10 text-[13px] text-[#080707] font-medium outline-none focus:border-[#0176D3] transition-all cursor-pointer disabled:opacity-60 focus:ring-1 focus:ring-[#0176D3]"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#706E6B] pointer-events-none" />
    </div>
  );
}

const EMPTY_FORM = { name: '', type: 'email', status: 'draft', triggerEvent: 'manual', triggerScoreMin: 75, description: '', subject: '' };

export default function CrmCampaignsPage() {
  const [loading, setLoading] = useState(true);
  const [sequences, setSequences] = useState([]);
  const [activeTab, setActiveTab] = useState('sequences');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/crm/campaigns');
      setSequences(res.data.sequences || []);
    } catch (err) {
      console.error('Failed to load campaigns', err);
      alertService.toast('error', 'Failed to load campaign sequences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (seq) => {
    setEditing(seq);
    setForm({ name: seq.name, type: seq.type, status: seq.status, triggerEvent: seq.triggerEvent, triggerScoreMin: seq.triggerScoreMin, description: seq.description || '', subject: seq.subject || '' });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/crm/campaigns/${editing._id}`, form);
        alertService.toast('success', 'Sequence updated');
      } else {
        await api.post('/crm/campaigns', form);
        alertService.toast('success', 'Sequence created');
      }
      setShowModal(false);
      load();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to save sequence');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (seq) => {
    const newStatus = seq.status === 'active' ? 'paused' : 'active';
    try {
      await api.put(`/crm/campaigns/${seq._id}`, { ...seq, status: newStatus });
      alertService.toast('success', `Sequence ${newStatus === 'active' ? 'activated' : 'paused'}`);
      load();
    } catch (err) {
      alertService.error('Error', 'Failed to update status');
    }
  };

  const handleDelete = async (seq) => {
    const confirmed = await alertService.confirm(
      'Delete Sequence?',
      `"${seq.name}" will be permanently removed.`,
      'warning',
      'Delete'
    );
    if (!confirmed) return;
    try {
      await api.delete(`/crm/campaigns/${seq._id}`);
      alertService.toast('success', 'Sequence deleted');
      load();
    } catch (err) {
      alertService.error('Error', 'Failed to delete sequence');
    }
  };

  return (
    <div className={PAGE}>
      {/* Header */}
      <PageHeader
        title="Campaign Sequences"
        subtitle="Automate email nurturing and SMS triggers based on lead interactions and scoring events."
        icon={Zap}
        actions={<>
<button
          onClick={openCreate}
          className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all flex items-center gap-2 shrink-0 whitespace-nowrap"
        >
          <Plus size={14} /> New Sequence
        </button>
  </>}
      />

      {/* Tabs */}
      <Tabs tabs={[{ key: 'sequences', label: 'Active Sequences' }, { key: 'integration', label: 'SMTP & SMS Integration' }]} value={activeTab} onChange={setActiveTab} />

      {/* Sequences Tab */}
      {activeTab === 'sequences' && (
        <div>
          {loading ? (
            <PageLoader inline />
          ) : sequences.length === 0 ? (
            <div className="sf-card text-center py-16 space-y-3">
              <div className="w-14 h-14 bg-[#EAF4FE] rounded-full flex items-center justify-center mx-auto">
                <Zap size={26} className="text-[#0176D3]" />
              </div>
              <p className="font-bold text-[#080707] text-[13px]">No sequences yet</p>
              <p className="text-[12px] text-[#706E6B]">Create your first email or SMS campaign sequence</p>
              <button
                onClick={openCreate}
                className="mt-2 h-9 px-4 inline-flex items-center gap-2 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] transition-all whitespace-nowrap shrink-0"
              >
                <Plus size={14} /> New Sequence
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {sequences.map(seq => {
                const typeMeta = TYPE_META[seq.type] || TYPE_META.email;
                const TypeIcon = typeMeta.icon;
                return (
                  <div key={seq._id} className="sf-card p-5 flex flex-col hover:border-[#0176D3] transition-all group">
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="p-2 rounded border" style={{ background: typeMeta.bg, color: typeMeta.color, borderColor: typeMeta.border }}>
                        <TypeIcon size={16} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={cn('sf-badge', STATUS_BADGE[seq.status] || 'sf-badge-neutral')}>{seq.status}</span>
                        <button
                          onClick={() => handleToggleStatus(seq)}
                          title={seq.status === 'active' ? 'Pause' : 'Activate'}
                          aria-label={seq.status === 'active' ? 'Pause' : 'Activate'}
                          className="h-7 w-7 flex items-center justify-center rounded border border-[#D8DDE6] hover:bg-[#F3F2F2] hover:text-[#0176D3] text-[#706E6B] transition-all"
                        >
                          {seq.status === 'active' ? <Pause size={13} /> : <Play size={13} />}
                        </button>
                      </div>
                    </div>

                    <h3 className="font-bold text-[#080707] text-[14px] leading-snug mb-1">{seq.name}</h3>
                    {seq.description && <p className="text-[12px] text-[#706E6B] mb-3 line-clamp-2">{seq.description}</p>}

                    <div className="mt-auto space-y-2 pt-3 border-t border-[#D8DDE6]">
                      <div>
                        <p className="text-[10px] text-[#706E6B] font-bold uppercase tracking-wider">Trigger</p>
                        <p className="text-[12px] text-[#080707] font-medium">{seq.triggerLabel || seq.triggerEvent}</p>
                      </div>
                      {seq.type === 'email' && seq.subject && (
                        <div>
                          <p className="text-[10px] text-[#706E6B] font-bold uppercase tracking-wider">Subject</p>
                          <p className="text-[12px] text-[#080707] truncate">{seq.subject}</p>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                      <button
                        onClick={() => openEdit(seq)}
                        title="Edit sequence"
                        aria-label="Edit sequence"
                        className="h-8 w-8 flex items-center justify-center bg-white border border-[#D8DDE6] rounded hover:bg-[#F3F2F2] hover:border-[#0176D3] hover:text-[#0176D3] text-[#706E6B] transition-all"
                      >
                        <Edit2 size={13} />
                      </button>
                      <button
                        onClick={() => handleDelete(seq)}
                        title="Delete sequence"
                        aria-label="Delete sequence"
                        className="h-8 w-8 flex items-center justify-center bg-white border border-[#D8DDE6] rounded hover:bg-[#FDE7E9] hover:border-[#BA0517] hover:text-[#BA0517] text-[#706E6B] transition-all"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Integration Tab */}
      {activeTab === 'integration' && (
        <div className="max-w-3xl space-y-4">
          {[
            {
              icon: Mail,
              color: '#0176D3',
              bg: '#EAF4FE',
              title: 'SMTP Email Server',
              desc: 'Used for welcome emails, drip nurturing, and system notification alerts.',
              link: '/dashboard/settings?tab=email',
              linkLabel: 'SMTP Config',
            },
            {
              icon: MessageSquare,
              color: '#2E844A',
              bg: '#EBF3ED',
              title: 'SMS / Twilio Integration',
              desc: 'Used for instant SMS alert notifications on hot leads and event updates.',
              link: '/dashboard/settings',
              linkLabel: 'SMS Settings',
            },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="sf-card p-6 flex items-center justify-between gap-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded" style={{ background: item.bg, color: item.color }}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#080707]">{item.title}</h3>
                    <p className="text-[#706E6B] text-[13px] mt-0.5">{item.desc}</p>
                  </div>
                </div>
                <Link
                  to={item.link}
                  className="h-9 px-4 flex items-center gap-1.5 bg-white border border-[#D8DDE6] hover:bg-[#F3F2F2] text-[#080707] text-[12px] font-bold rounded transition-all shrink-0"
                >
                  {item.linkLabel} <ChevronRight size={14} />
                </Link>
              </div>
            );
          })}

          <div className="sf-card p-5 bg-[#EAF4FE] border-[#B8DAF3] flex items-start gap-3">
            <AlertTriangle size={18} className="text-[#0176D3] mt-0.5 shrink-0" />
            <div>
              <p className="text-[13px] font-bold text-[#014486] mb-1">Configure integrations first</p>
              <p className="text-[12px] text-[#0176D3]">
                Before activating email or SMS sequences, configure your SMTP server and Twilio credentials in Settings.
                Sequences will automatically use these credentials when triggered.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">
                {editing ? 'Edit Sequence' : 'Create Campaign Sequence'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-[#706E6B] hover:text-[#080707]">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSave}>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-semibold text-[#706E6B]">Sequence Name *</label>
                  <input
                    required
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Welcome Nurture Sequence"
                    className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-semibold text-[#706E6B]">Type</label>
                    <StyledSelect
                      value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                      options={[
                        { value: 'email', label: 'Email' },
                        { value: 'sms', label: 'SMS' },
                        { value: 'mixed', label: 'Mixed' },
                      ]}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-semibold text-[#706E6B]">Status</label>
                    <StyledSelect
                      value={form.status}
                      onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                      options={[
                        { value: 'draft', label: 'Draft' },
                        { value: 'active', label: 'Active' },
                        { value: 'paused', label: 'Paused' },
                      ]}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[11px] font-semibold text-[#706E6B]">Trigger Rule</label>
                  <StyledSelect
                    value={form.triggerEvent}
                    onChange={e => setForm(f => ({ ...f, triggerEvent: e.target.value }))}
                    options={TRIGGERS}
                  />
                </div>

                {form.triggerEvent === 'score_threshold' && (
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-semibold text-[#706E6B]">Minimum Score</label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={form.triggerScoreMin}
                      onChange={e => setForm(f => ({ ...f, triggerScoreMin: +e.target.value }))}
                      className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
                    />
                  </div>
                )}

                {form.type !== 'sms' && (
                  <div className="space-y-1.5">
                    <label className="block text-[11px] font-semibold text-[#706E6B]">Email Subject</label>
                    <input
                      value={form.subject}
                      onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                      placeholder="e.g. Welcome to our platform, {{leadName}}!"
                      className="w-full h-9 px-4 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all focus:ring-1 focus:ring-[#0176D3]"
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="block text-[11px] font-semibold text-[#706E6B]">Description (optional)</label>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Briefly describe the purpose of this sequence…"
                    className="w-full px-4 py-2.5 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] transition-all resize-none focus:ring-1 focus:ring-[#0176D3]"
                  />
                </div>
              </div>

              <div className="px-6 py-4 border-t border-[#D8DDE6] bg-[#F4F6F9] flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="h-9 px-4 rounded border border-[#D8DDE6] text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] whitespace-nowrap shrink-0"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || !form.name.trim()}
                  className="h-9 px-5 rounded bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold transition-all disabled:opacity-50 flex items-center gap-2 whitespace-nowrap shrink-0"
                >
                  {saving ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
